import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useBusinesses } from "@/hooks/use-businesses";
import {
  analyzeFinancials, listWorkflows, getQueue,
  approveAction, rejectAction
} from "@/services/aiAgentApi";
import {
  CheckCircle, Clock, AlertTriangle, Download, TrendingUp,
  TrendingDown, Brain, Loader2, FileText
} from "lucide-react";

export default function ClientAIDashboard() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const businessId = activeBusiness?.id;
  const year = new Date().getFullYear();
  const today = new Date().toISOString().split("T")[0];

  const { data: analysis, isLoading: analysisLoading } = useQuery({
    queryKey: ["/client/analysis", businessId],
    queryFn: () => analyzeFinancials(`${year}-01-01`, today, businessId),
    enabled: !!businessId,
    staleTime: 60000,
  });

  const { data: workflows = [] } = useQuery({
    queryKey: ["/client/workflows", businessId],
    queryFn: () => listWorkflows(businessId),
    enabled: !!businessId,
    refetchInterval: 15000,
  });

  const { data: pending = [] } = useQuery({
    queryKey: ["/client/queue", businessId],
    queryFn: () => getQueue(businessId, "pending"),
    enabled: !!businessId,
    refetchInterval: 10000,
  });

  const approve = useMutation({
    mutationFn: (id: number) => approveAction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/client/queue"] });
      toast({ title: "Approved" });
    },
  });

  const reject = useMutation({
    mutationFn: (id: number) => rejectAction(id, "Client rejected"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/client/queue"] });
      toast({ title: "Rejected" });
    },
  });

  // Derive books status
  const latestWf = workflows[0];
  const booksStatus = !latestWf ? "not_started" :
    latestWf.status === "completed" ? "ready" :
    latestWf.status === "running" ? "in_progress" :
    latestWf.status === "paused" ? "needs_attention" : "not_started";

  const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
    ready: { label: "Books are up to date", color: "bg-green-100 text-green-800", icon: CheckCircle },
    in_progress: { label: "AI is processing your books", color: "bg-blue-100 text-blue-800", icon: Loader2 },
    needs_attention: { label: "Your attention is needed", color: "bg-yellow-100 text-yellow-800", icon: AlertTriangle },
    not_started: { label: "No activity yet", color: "bg-gray-100 text-gray-800", icon: Clock },
  };

  const sc = statusConfig[booksStatus];
  const StatusIcon = sc.icon;

  if (!businessId) {
    return <Layout><div className="p-8 text-center text-muted-foreground">Select a business to continue.</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-primary/10 rounded-xl"><Brain className="h-7 w-7 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-bold">Your Books</h1>
            <p className="text-muted-foreground text-sm">{activeBusiness?.name}</p>
          </div>
        </div>

        {/* Books Status */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${sc.color}`}>
                <StatusIcon className={`h-6 w-6 ${booksStatus === "in_progress" ? "animate-spin" : ""}`} />
              </div>
              <div className="flex-1">
                <div className="text-lg font-semibold">{sc.label}</div>
                {latestWf && (
                  <p className="text-sm text-muted-foreground">
                    Last updated: {new Date(latestWf.updated_at || latestWf.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <Badge className={sc.color}>{booksStatus.replace("_", " ")}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Quick Numbers */}
        {analysisLoading ? (
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        ) : analysis?.success ? (
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <TrendingUp className="h-4 w-4" /> Money In
                </div>
                <div className="text-2xl font-bold text-green-600">
                  ${(analysis.data?.revenue || 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                  <TrendingDown className="h-4 w-4" /> Money Out
                </div>
                <div className="text-2xl font-bold text-red-600">
                  ${(analysis.data?.expenses || 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-muted-foreground text-sm mb-1">Profit / Loss</div>
                <div className={`text-2xl font-bold ${(analysis.data?.netIncome || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                  ${(analysis.data?.netIncome || 0).toLocaleString()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-muted-foreground text-sm mb-1">Needs Review</div>
                <div className={`text-2xl font-bold ${(analysis.data?.uncategorizedCount || 0) > 0 ? "text-yellow-600" : "text-green-600"}`}>
                  {analysis.data?.uncategorizedCount || 0} items
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}

        {/* Pending Approvals */}
        {pending.length > 0 && (
          <Card className="border-yellow-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                Items Needing Your Approval
                <Badge variant="destructive">{pending.length}</Badge>
              </CardTitle>
              <CardDescription>AI has categorized these transactions. Please review.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {pending.slice(0, 10).map((action: any) => (
                <div key={action.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="text-sm">
                    <span className="font-medium">{action.action_type.replace(/_/g, " ")}</span>
                    {action.payload?.transaction_id && <span className="text-muted-foreground"> — Transaction #{action.payload.transaction_id}</span>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approve.mutate(action.id)} disabled={approve.isPending} className="gap-1">
                      <CheckCircle className="h-3 w-3" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reject.mutate(action.id)} disabled={reject.isPending}>
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Insights (simple language) */}
        {analysis?.insights && analysis.insights.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">What You Should Know</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {analysis.insights.map((insight: any, i: number) => (
                <div key={i} className={`p-3 rounded-lg border text-sm ${
                  insight.type === "warning" ? "bg-yellow-50 border-yellow-200" :
                  insight.type === "positive" ? "bg-green-50 border-green-200" :
                  "bg-blue-50 border-blue-200"
                }`}>
                  {insight.type === "warning" ? "⚠️" : insight.type === "positive" ? "✅" : "💡"} {insight.message}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Download Reports */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5" /> Your Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="flex gap-3 flex-wrap">
            <Button variant="outline" className="gap-2" onClick={() => window.open(`/api/businesses/${businessId}/reports/profit-loss/download?startDate=${year}-01-01&endDate=${today}`, "_blank")}>
              <Download className="h-4 w-4" /> Profit & Loss
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => window.open(`/api/businesses/${businessId}/reports/balance-sheet/download?startDate=${year}-01-01&endDate=${today}`, "_blank")}>
              <Download className="h-4 w-4" /> Balance Sheet
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
